import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, message, Spin, InputNumber, Row, Col, Divider, Checkbox, Typography, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { INVOICE_API, CONTRACT_API } from '../../constants/api';
import axios from '../../services/axiosConfig';
import dayjs from 'dayjs';

const { Text } = Typography;

const EditInvoiceModal = ({ visible, record, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [fileList, setFileList] = useState([]);

  const fetchContracts = async () => {
    try {
      const { data } = await axios.get(CONTRACT_API.GET_ACTIVE_CONTRACTS);
      setContracts(data.data.map(contract => {
        return {
          value: contract.ContractID,
          label: `${contract.ContractID} - KH: ${contract.Tenant?.TenantName || 'N/A'} - Xưởng: ${contract.Factory?.FactoryName || 'N/A'}`,
          contract: contract
        };
      }));
    } catch (error) {
      console.error('Lỗi khi tải danh sách hợp đồng:', error);
      message.error('Không thể tải danh sách hợp đồng');
    }
  };

  const fetchInvoiceDetail = async () => {
    if (!record || !record.id) return;

    try {
      setLoading(true);
      const response = await axios.get(INVOICE_API.DETAIL(record.id));
      const detail = response.data.data;

      const isDebt = detail.IsDebt === true || detail.IsDebt === 1;

      form.setFieldsValue({
        invoiceCode: detail.InvoiceID,
        contractId: detail.ContractID,
        period: detail.Period,
        creationDate: detail.CreationDate ? dayjs(detail.CreationDate) : dayjs(detail.CreatedAt),
        paymentDueDate: detail.DueDate ? dayjs(detail.DueDate) : null,
        rentAmount: detail.RentAmount || 0,
        serviceAmount: detail.ServiceAmount || 0,
        electricityAmount: detail.ElectricityAmount || 0,
        waterAmount: detail.WaterAmount || 0,
        otherAmount: detail.OtherAmount || 0,
        isDebt: isDebt,
        note: detail.Note || '',
      });

      setTotalAmount(detail.Amount || 0);

      // Khôi phục file đính kèm
      let initialFileList = [];
      if (detail.Files) {
        try {
          const files = typeof detail.Files === 'string' ? JSON.parse(detail.Files) : detail.Files;
          initialFileList = files.map((filename, index) => ({
            uid: `-${index}`,
            name: filename,
            status: 'done',
            url: `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/uploads/invoices/${filename}`
          }));
        } catch (e) {
          console.error('Lỗi khi parse file list', e);
        }
      }
      setFileList(initialFileList);

      // Khôi phục selected contract
      const contractItem = contracts.find(c => c.value === detail.ContractID);
      if (contractItem) {
        setSelectedContract(contractItem.contract);
      }
    } catch (error) {
      console.error('Lỗi khi tải chi tiết hóa đơn:', error);
      message.error('Không thể tải chi tiết hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    if (visible && record && contracts.length > 0) {
      fetchInvoiceDetail();
    } else {
      form.resetFields();
      setTotalAmount(0);
      setSelectedContract(null);
      setFileList([]);
    }
  }, [visible, record, contracts, form]);

  const handleContractChange = (value) => {
    const contractItem = contracts.find(c => c.value === value);
    const contract = contractItem?.contract;
    setSelectedContract(contract);
  };

  const handleValuesChange = (changedValues, allValues) => {
    const rent = allValues.rentAmount || 0;
    const service = allValues.serviceAmount || 0;
    const elec = allValues.electricityAmount || 0;
    const water = allValues.waterAmount || 0;
    const other = allValues.otherAmount || 0;

    setTotalAmount(rent + service + elec + water + other);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('period', values.period);
      if (values.paymentDueDate) formData.append('paymentDueDate', values.paymentDueDate.format('YYYY-MM-DD'));
      formData.append('rentAmount', values.rentAmount || 0);
      formData.append('serviceAmount', values.serviceAmount || 0);
      formData.append('electricityAmount', values.electricityAmount || 0);
      formData.append('waterAmount', values.waterAmount || 0);
      formData.append('otherAmount', values.otherAmount || 0);
      formData.append('isDebt', values.isDebt ? true : false);
      formData.append('note', values.note || '');

      const retainedFiles = fileList.filter(f => f.url).map(f => f.name);
      formData.append('retainedFiles', JSON.stringify(retainedFiles));

      const newFiles = fileList.filter(f => !f.url);
      newFiles.forEach(file => {
        formData.append('invoiceFiles', file.originFileObj || file);
      });

      await axios.put(INVOICE_API.UPDATE(record.id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      message.success('Cập nhật hóa đơn thành công');
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Lỗi khi cập nhật hóa đơn:', error);
      message.error('Không thể cập nhật hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Chỉnh sửa hóa đơn tổng hợp"
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      width={900}
      confirmLoading={loading}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleValuesChange}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contractId"
                label="Hợp đồng"
                rules={[{ required: true, message: 'Vui lòng chọn hợp đồng' }]}
              >
                <Select
                  placeholder="Chọn hợp đồng"
                  options={contracts}
                  onChange={handleContractChange}
                  disabled // Không cho sửa hợp đồng khi edit
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="invoiceCode"
                label="Mã hóa đơn"
                rules={[{ required: true, message: 'Vui lòng nhập mã hóa đơn' }]}
              >
                <Input disabled placeholder="VD: INV-05-2024" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="period"
                label="Kỳ hóa đơn (Tháng/Năm)"
                rules={[{ required: true, message: 'Vui lòng nhập kỳ hóa đơn' }]}
              >
                <Input placeholder="VD: 05/2024" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="creationDate"
                label="Ngày tạo hóa đơn"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="paymentDueDate"
                label="Hạn thanh toán"
                rules={[{ required: true, message: 'Vui lòng chọn hạn thanh toán' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Chi tiết các khoản thu</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="rentAmount" label="Tiền thuê xưởng">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="serviceAmount" label="Phí dịch vụ & hạ tầng">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="electricityAmount" label="Tiền điện">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="waterAmount" label="Tiền nước sạch">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="otherAmount" label="Tiền nước thải (nếu có)">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <div style={{ marginTop: '30px', textAlign: 'right' }}>
                <Text type="secondary">Tổng cộng: </Text>
                <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                  {new Intl.NumberFormat('vi-VN').format(totalAmount)} VNĐ
                </Text>
              </div>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="isDebt" valuePropName="checked">
                <Checkbox>Đánh dấu đây là khoản công nợ (Khách nợ chưa trả)</Checkbox>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="note" label="Ghi chú">
                <Input.TextArea rows={3} placeholder="Nhập ghi chú cho hóa đơn..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="File đính kèm">
                <Upload
                  multiple
                  beforeUpload={(file) => {
                    setFileList([...fileList, file]);
                    return false;
                  }}
                  onRemove={(file) => {
                    const index = fileList.indexOf(file);
                    const newFileList = fileList.slice();
                    newFileList.splice(index, 1);
                    setFileList(newFileList);
                  }}
                  fileList={fileList}
                >
                  <Button icon={<UploadOutlined />}>Chọn file (PDF, hình ảnh)</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditInvoiceModal;